/**
 * useRoyaleTournaments / useRoyaleTournamentDetail
 *
 * GET /fun_challenges/v1/royale_tournaments/?state=scheduled,live
 * GET /fun_challenges/v1/royale_tournaments/<id>/
 *
 * Reads for the staff Host panel (all scheduled/live tournaments) and the
 * tournaments-page discovery + completed history view. Lifecycle writes
 * (create/start/cancel/register/withdraw) go through the SOCKET (useRoyaleSocket),
 * not here — this hook is read-only. The durable numeric `id` is the identity;
 * `session_id` links completed results. All datetimes are UTC ISO strings.
 */

import {useQuery} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';
import {noteServerNow} from '@/Screens/FunChallenges/util/serverClock';

export type RoyaleTournamentState =
  | 'scheduled'
  | 'live'
  | 'completed'
  | 'cancelled';

export interface RoyaleTournamentSummary {
  id: number;
  state: RoyaleTournamentState;
  share_code: string | null;
  session_id: string | null;
  room_id: string | null;
  created_by: number;
  created_by_name: string | null;
  scheduled_at: string | null; // UTC ISO
  started_at: string | null;
  ended_at: string | null;
  max_players: number;
  timer_sec: number;
  num_puzzles: number | null;
  topics: string[];
  difficulty: 'easy' | 'medium' | 'hard';
  custom_puzzles: boolean;
  name: string | null;          // host title; null/blank => "Puzzle Royale #id"
  description: string | null;   // optional Details blurb
  is_private: boolean;          // FUTURE: not yet enforced
  has_password: boolean;        // whether a password is set (raw pw never sent)
  player_count: number | null;
}

export interface RoyaleTournamentRosterEntry {
  user_id: number;
  user_name: string | null;
  status: 'registered' | 'withdrawn';
  registered_at: string | null;
}

export interface RoyaleTournamentPlayerResult {
  user_id: number;
  points: number;
  score: number;
  wrong: number;
  best_combo: number;
  piece: string;
  rank: number;
  accuracy: number;
  solved_ids: number[];
  failed_ids: number[];
  attempts: unknown[];
  eliminated: boolean;
  finished_at: string | null;
}

export interface RoyaleTournamentDetail extends RoyaleTournamentSummary {
  roster: RoyaleTournamentRosterEntry[];
  /**
   * Server wall clock (epoch ms) at response time — used to correct device
   * clock skew before rendering the "Game starts in" countdown. Optional: an
   * older Django build omits it, in which case the offset simply isn't updated.
   */
  server_now?: number;
  results:
    | (Record<string, unknown> & {
        players: RoyaleTournamentPlayerResult[];
        puzzle_royale_detail: Record<string, unknown> | null;
      })
    | null;
}

/** List tournaments by state (host panel + discovery). Default state omitted =>
 *  server returns the scheduled+live set. Poll while a lobby is open so the
 *  roster count + state changes surface without a manual refresh. */
export function useRoyaleTournaments(
  states?: RoyaleTournamentState[],
  opts?: {refetchIntervalMs?: number; enabled?: boolean; mine?: boolean},
) {
  const stateParam = (states && states.length ? states.join(',') : '');
  const mine = !!opts?.mine;
  return useQuery<RoyaleTournamentSummary[], unknown>(
    ['royale_tournaments', stateParam, mine ? 'mine' : 'all'],
    async () => {
      const params: string[] = [];
      if (stateParam) params.push(`state=${encodeURIComponent(stateParam)}`);
      if (mine) params.push('mine=1');
      const qs = params.length ? `?${params.join('&')}` : '';
      const res = await ChessRestClient.get(
        `fun_challenges/v1/royale_tournaments/${qs}`,
      );
      return (res.data as RoyaleTournamentSummary[]) || [];
    },
    {
      enabled: opts?.enabled !== false,
      refetchInterval: opts?.refetchIntervalMs,
      staleTime: 5_000,
    },
  );
}

/** One tournament by durable id: summary + roster + (if completed) results. */
export function useRoyaleTournamentDetail(
  tournamentId: number | string | null | undefined,
  opts?: {refetchIntervalMs?: number; enabled?: boolean},
) {
  const idStr = tournamentId != null ? String(tournamentId) : '';
  return useQuery<RoyaleTournamentDetail, unknown>(
    ['royale_tournament_detail', idStr],
    async () => {
      const res = await ChessRestClient.get(
        `fun_challenges/v1/royale_tournaments/${idStr}/`,
      );
      // Calibrate the device↔server clock skew from Django's `server_now`
      // (epoch ms, additive). This screen renders "Game starts in m:ss" BEFORE
      // any socket envelope arrives, so without this a cold page load on a
      // mis-set device had no offset and mis-rendered the countdown.
      // Deliberately inside `queryFn`: it runs only on a real network response,
      // never on a react-query cache hit (whose timestamp would be stale).
      noteServerNow((res.data as any)?.server_now);
      return res.data as RoyaleTournamentDetail;
    },
    {
      enabled: !!idStr && opts?.enabled !== false,
      refetchInterval: opts?.refetchIntervalMs,
      staleTime: 5_000,
    },
  );
}
